// Account overview summary cards — FE-039.

import { Link } from "react-router-dom";

import { buildCommitmentFilterUrl } from "@/features/dashboard";

import type { AccountDetail } from "./types";


interface AccountOverviewCardsProps {
  account: AccountDetail;
}


type CardKey =
  | "open"
  | "overdue"
  | "blocked"
  | "completion"
  | "on_time"
  | "employees";


interface CardConfig {
  key: CardKey;
  label: string;
  helper?: string;
  href?: string;
  value: string;
  accent?: string;
}


export function AccountOverviewCards({ account }: AccountOverviewCardsProps) {
  const id = account.account_id;
  const cards: CardConfig[] = [
    {
      key: "open",
      label: "Open Commitments",
      helper: "Backlog through in-review.",
      value: formatCount(account.open_commitments),
      href: buildCommitmentFilterUrl({ client_id: id }),
    },
    {
      key: "overdue",
      label: "Overdue",
      helper: "Past their due date and still open.",
      value: formatCount(account.overdue_commitments),
      href: buildCommitmentFilterUrl({ client_id: id, overdue: true }),
      accent: "text-ember",
    },
    {
      key: "blocked",
      label: "Blocked",
      helper: "Open work waiting on something.",
      value: formatCount(account.blocked_commitments),
      href: buildCommitmentFilterUrl({ client_id: id, is_blocked: true }),
      accent: "text-blocked",
    },
    {
      key: "completion",
      label: "Completion rate",
      helper: "Done divided by done plus cancelled.",
      value: formatPercent(account.completion_rate, true),
      accent: "text-sage",
    },
    {
      key: "on_time",
      label: "On-time",
      helper: "Closed on or before due date.",
      value: formatPercent(account.on_time_percent, false),
    },
    {
      key: "employees",
      label: "Employees",
      helper: "Total headcount on file.",
      value: formatCount(account.employee_count_total),
    },
  ];
  return (
    <ul
      data-testid="account-overview-cards"
      role="list"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {cards.map((card) => (
        <li key={card.key}>
          <OverviewCard card={card} />
        </li>
      ))}
    </ul>
  );
}


function OverviewCard({ card }: { card: CardConfig }) {
  const body = (
    <>
      <span className="block text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {card.label}
      </span>
      <span
        data-testid={`account-overview-value-${card.key}`}
        className={`mt-1 block text-xl font-semibold ${card.accent ?? "text-text-primary"}`}
      >
        {card.value}
      </span>
      {card.helper && (
        <span className="mt-1 block text-xs text-text-secondary">
          {card.helper}
        </span>
      )}
    </>
  );
  const baseClasses =
    "block rounded-lg bg-recessed px-4 py-3 transition-fast ease-out";
  if (card.href) {
    return (
      <Link
        to={card.href}
        data-testid={`account-overview-card-${card.key}`}
        aria-label={`${card.label}: ${card.value}`}
        className={`${baseClasses} hover:bg-raised`}
      >
        {body}
      </Link>
    );
  }
  return (
    <div
      data-testid={`account-overview-card-${card.key}`}
      aria-label={`${card.label}: ${card.value}`}
      className={baseClasses}
    >
      {body}
    </div>
  );
}


function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return String(value);
}


function formatPercent(
  value: number | null | undefined,
  isFraction: boolean,
): string {
  if (value === null || value === undefined) return "—";
  const pct = isFraction ? value * 100 : value;
  return `${Math.round(pct)}%`;
}
